import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Redirect, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaHome,
  FaLayerGroup,
  FaMagic,
  FaPlus,
  FaPrint,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

//Created By Cursor
const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const BRAND = {
  navy: "#0f172a",
  bg: "#f5f7fb",
};

const TYPE_LABELS = {
  mcq: "MCQ",
  short: "Short Answer",
  long: "Long Answer",
  fill_blank: "Fill in the Blank",
  match: "Match the Following",
  true_false: "True / False",
};

const STATUS_STYLES = {
  draft: { bg: "#EEF1F8", fg: "#475569", label: "Draft" },
  submitted: { bg: "#FFF6E9", fg: "#B5750B", label: "Submitted" },
  locked: { bg: "#E6F8F6", fg: "#0F8A7E", label: "Locked" },
  approved: { bg: "#E6F8F6", fg: "#0F8A7E", label: "Approved" },
};

//Created By Cursor
function subjectsForClass(classId, rows = []) {
  const seen = new Map();
  rows
    .filter((r) => String(r.class_id) === String(classId))
    .forEach((r) => {
      if (!seen.has(String(r.subject_id))) seen.set(String(r.subject_id), r);
    });
  return Array.from(seen.values());
}

//Created By Cursor
function subjectLabel(row) {
  return row?.subject_name || row?.book_name || row?.subject_id || "Subject";
}

//Created By Cursor
function sectionTotal(sec) {
  return Number(sec.question_count || 0) * Number(sec.marks_each || 0);
}

//Created By Cursor — blank display for cleared number inputs; use toNum() for math/API
function numInputValue(value) {
  if (value === "" || value == null) return "";
  return value;
}

function toNum(value) {
  if (value === "" || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function handleNumChange(raw, onCommit) {
  if (raw === "" || raw == null) {
    onCommit("");
    return;
  }
  const n = Number(raw);
  onCommit(Number.isFinite(n) ? n : "");
}

function handleNumBlur(value, onCommit) {
  if (value === "" || value == null) {
    onCommit(0);
  }
}

//Created By Cursor
function emptySection(index = 0) {
  return {
    local_id: `s_${Date.now()}_${index}`,
    section_label: `Section ${String.fromCharCode(65 + index)}`,
    question_type: "mcq",
    question_count: 0,
    marks_each: 0,
    section_instructions: "",
  };
}

//Created By Cursor
function Pill({ bg, fg, children }) {
  return (
    <span className="qb-pill" style={{ background: bg, color: fg }}>
      {children}
    </span>
  );
}

//Created By Cursor
function StageStepper({ stage }) {
  return (
    <nav className="pb-stepper" aria-label="Paper builder stages">
      <span className={`pb-stepper__item${stage === 1 ? " is-active" : ""}`}>
        <span className="pb-stepper__num">1</span>
        Structure
      </span>
      <span className="pb-stepper__rule" aria-hidden />
      <span className={`pb-stepper__item${stage === 2 ? " is-active" : ""}`}>
        <span className="pb-stepper__num">2</span>
        Assemble
      </span>
    </nav>
  );
}

//Created By Cursor
function Banner({ ok, children }) {
  return (
    <div className={`pb-banner ${ok ? "is-ok" : "is-warn"}`}>
      {ok ? <FaCheckCircle /> : <FaExclamationTriangle />}
      <span>{children}</span>
    </div>
  );
}

//Created By Cursor
function QuestionPickerModal({ paper, section, onClose, onPick, busy }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [chapter, setChapter] = useState("");
  const [difficulty, setDifficulty] = useState("All");
  const [matchMarks, setMatchMarks] = useState(false);
  const [typeFilter, setTypeFilter] = useState(section.question_type || "All");

  //Created By Cursor
  useEffect(() => {
    setTypeFilter(section.question_type || "All");
  }, [section.question_type, section.id]);

  //Created By Cursor
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) return;
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE()}getQuestionBank.php`, {
          params: {
            school_code: schoolCode,
            class_id: paper.class_id || undefined,
            subject_id: paper.subject_id || undefined,
            type:
              typeFilter && typeFilter !== "All" ? typeFilter : undefined,
            marks: matchMarks ? section.marks_each : undefined,
            chapter: chapter || undefined,
            difficulty: difficulty === "All" ? undefined : difficulty,
            search: search || undefined,
          },
        });
        if (!cancelled) {
          const data =
            res.data?.api_status === "OK" && Array.isArray(res.data.data)
              ? res.data.data
              : [];
          const taken = new Set(
            (section.items || []).map((i) => String(i.question_bank_id))
          );
          setRows(data.filter((q) => !taken.has(String(q.id))));
        }
      } catch (err) {
        console.error("[PaperBuilder] picker error:", err);
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    paper.class_id,
    paper.subject_id,
    section.items,
    section.marks_each,
    search,
    chapter,
    difficulty,
    matchMarks,
    typeFilter,
  ]);

  //Created By Cursor
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const modal = (
    <div className="payroll-page qb-page pb-page">
      <div
        className="qb-modal-backdrop"
        role="dialog"
        aria-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="qb-modal pb-picker qb-modal--wide">
          <div className="qb-modal__head">
            <div className="qb-modal__head-copy">
              <p className="qb-modal__eyebrow">Question bank</p>
              <h2>Pick for {section.section_label}</h2>
              <p className="qb-modal__sub">
                Target:{" "}
                {TYPE_LABELS[section.question_type] || section.question_type} ·{" "}
                {section.marks_each} mark(s) each
              </p>
            </div>
            <button
              type="button"
              className="qb-icon-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
          <div className="qb-modal__body">
            <div className="pb-picker-filters">
              <div className="qb-search">
                <FaSearch />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search questions…"
                  aria-label="Search questions"
                />
              </div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Type filter"
              >
                <option value="All">All types</option>
                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
              <input
                className="qb-chapter-input"
                value={chapter}
                onChange={(e) => setChapter(e.target.value)}
                placeholder="Chapter"
                aria-label="Chapter filter"
              />
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
                aria-label="Difficulty filter"
              >
                {["All", "Easy", "Medium", "Hard"].map((d) => (
                  <option key={d} value={d}>
                    {d === "All" ? "All difficulty" : d}
                  </option>
                ))}
              </select>
              <label className="pb-picker-check">
                <input
                  type="checkbox"
                  checked={matchMarks}
                  onChange={(e) => setMatchMarks(e.target.checked)}
                />
                Match {section.marks_each} mark(s) only
              </label>
            </div>
            {loading ? (
              <MyLoader />
            ) : rows.length === 0 ? (
              <p className="qb-empty">
                No matching questions in the bank for this class/subject.
                {matchMarks
                  ? " Try unchecking “Match marks only”."
                  : " Add questions in Question Bank first."}
              </p>
            ) : (
              <div className="qb-list">
                {rows.map((q) => {
                  const marksDiff =
                    Number(q.marks) !== Number(section.marks_each);
                  return (
                    <article key={q.id} className="qb-card">
                      <div className="qb-card__body">
                        <p className="qb-card__text">{q.question_text}</p>
                        <div className="qb-card__meta">
                          <Pill bg="#EAF9F7" fg="#0F8A7E">
                            {TYPE_LABELS[q.type] || q.type}
                          </Pill>
                          <Pill bg="#EEF1F8" fg={BRAND.navy}>
                            {q.difficulty}
                          </Pill>
                          <Pill
                            bg={marksDiff ? "#FFF6E9" : "#F2F2F2"}
                            fg={marksDiff ? "#B5750B" : "#666"}
                          >
                            {q.marks} mark(s)
                            {marksDiff ? " ≠ section" : ""}
                          </Pill>
                          {q.chapter ? (
                            <Pill bg="#FFF6E9" fg="#B5750B">
                              {q.chapter}
                            </Pill>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="qb-btn qb-btn--primary"
                        disabled={busy}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onPick(q);
                        }}
                      >
                        {busy ? "Adding…" : "Add"}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

//Created By Cursor
function StructureStage({
  classes,
  classSubjects,
  meta,
  setMeta,
  sections,
  setSections,
  onContinue,
  saving,
}) {
  const subjectOptions = useMemo(
    () => subjectsForClass(meta.class_id, classSubjects),
    [meta.class_id, classSubjects]
  );
  const grandTotal = useMemo(
    () => sections.reduce((sum, s) => sum + sectionTotal(s), 0),
    [sections]
  );
  const marksMatch = Number(meta.max_marks || 0) === grandTotal;

  //Created By Cursor
  const updateSection = (localId, patch) => {
    setSections((prev) =>
      prev.map((s) => (s.local_id === localId ? { ...s, ...patch } : s))
    );
  };

  const canContinue =
    String(meta.exam_name || "").trim() &&
    meta.class_id &&
    meta.subject_id &&
    sections.length > 0 &&
    !saving;

  return (
    <div className="pb-stage">
      <div className="pb-stage__intro">
        <h2 className="pb-stage__title">Paper structure</h2>
        <p className="pb-stage__sub">
          Set exam details and section schema before pulling questions from the
          bank.
        </p>
      </div>

      <div className="qb-form qb-form--page">
        <label className="qb-form__full">
          <span>Exam name</span>
          <input
            value={meta.exam_name}
            onChange={(e) => setMeta({ ...meta, exam_name: e.target.value })}
            placeholder="e.g. Half Yearly — Physics"
          />
        </label>

        <div className="qb-meta-panel">
          <div className="qb-meta-panel__row">
            <label className="qb-field">
              <span>Class</span>
              <select
                value={meta.class_id}
                onChange={(e) => {
                  const cid = e.target.value;
                  const next = subjectsForClass(cid, classSubjects);
                  setMeta({
                    ...meta,
                    class_id: cid,
                    subject_id: next[0]?.subject_id || "",
                  });
                }}
              >
                <option value="">Select class</option>
                {classes.map((c) => (
                  <option key={c.class_id} value={c.class_id}>
                    {c.class_name}
                  </option>
                ))}
              </select>
            </label>
            <label className="qb-field">
              <span>Subject</span>
              <select
                value={meta.subject_id}
                onChange={(e) =>
                  setMeta({ ...meta, subject_id: e.target.value })
                }
                disabled={!meta.class_id}
              >
                <option value="">Select subject</option>
                {subjectOptions.map((s) => (
                  <option key={s.subject_id} value={s.subject_id}>
                    {subjectLabel(s)}
                  </option>
                ))}
              </select>
            </label>
            <label className="qb-field qb-field--marks">
              <span>Max marks</span>
              <input
                type="number"
                min={0}
                value={numInputValue(meta.max_marks)}
                onChange={(e) =>
                  handleNumChange(e.target.value, (n) =>
                    setMeta({ ...meta, max_marks: n })
                  )
                }
                onBlur={() =>
                  handleNumBlur(meta.max_marks, (n) =>
                    setMeta({ ...meta, max_marks: n })
                  )
                }
              />
            </label>
            <label className="qb-field qb-field--marks">
              <span>Duration (min)</span>
              <input
                type="number"
                min={0}
                value={numInputValue(meta.duration_minutes)}
                onChange={(e) =>
                  handleNumChange(e.target.value, (n) =>
                    setMeta({ ...meta, duration_minutes: n })
                  )
                }
                onBlur={() =>
                  handleNumBlur(meta.duration_minutes, (n) =>
                    setMeta({ ...meta, duration_minutes: n })
                  )
                }
              />
            </label>
            <label className="qb-field">
              <span>Exam date</span>
              <input
                type="date"
                value={meta.exam_date}
                onChange={(e) =>
                  setMeta({ ...meta, exam_date: e.target.value })
                }
              />
            </label>
            <label className="qb-field qb-field--chapter">
              <span>Blueprint name (optional)</span>
              <input
                value={meta.blueprint_name}
                onChange={(e) =>
                  setMeta({ ...meta, blueprint_name: e.target.value })
                }
                placeholder="Reusable pattern name"
              />
            </label>
          </div>
        </div>

        <label className="qb-form__full">
          <span>General instructions</span>
          <textarea
            rows={2}
            value={meta.general_instructions}
            onChange={(e) =>
              setMeta({ ...meta, general_instructions: e.target.value })
            }
            placeholder="All questions are compulsory…"
          />
        </label>
      </div>

      <div className="pb-sections-head">
        <div>
          <h3>Sections</h3>
          <p className="pb-sections-head__hint">
            {sections.length} section{sections.length === 1 ? "" : "s"} ·{" "}
            {grandTotal} marks planned
          </p>
        </div>
        <button
          type="button"
          className="qb-btn qb-btn--teal"
          onClick={() =>
            setSections((prev) => [...prev, emptySection(prev.length)])
          }
        >
          <FaPlus /> Add section
        </button>
      </div>

      <div className="pb-sections">
        {sections.map((sec, idx) => (
          <article key={sec.local_id} className="pb-section-card">
            <div className="pb-section-card__top">
              <span className="pb-section-card__badge" aria-hidden>
                {String.fromCharCode(65 + idx)}
              </span>
              <div className="pb-section-card__title">
                <strong>{sec.section_label || `Section ${idx + 1}`}</strong>
                <span>
                  {TYPE_LABELS[sec.question_type]} · {sectionTotal(sec)} marks
                </span>
              </div>
              {sections.length > 1 ? (
                <button
                  type="button"
                  className="qb-icon-btn qb-icon-btn--danger"
                  title="Remove section"
                  onClick={() =>
                    setSections((prev) =>
                      prev.filter((s) => s.local_id !== sec.local_id)
                    )
                  }
                >
                  <FaTrash />
                </button>
              ) : null}
            </div>
            <div className="qb-meta-panel pb-section-card__fields">
              <div className="qb-meta-panel__row">
                <label className="qb-field">
                  <span>Label</span>
                  <input
                    value={sec.section_label}
                    onChange={(e) =>
                      updateSection(sec.local_id, {
                        section_label: e.target.value,
                      })
                    }
                  />
                </label>
                <label className="qb-field">
                  <span>Type</span>
                  <select
                    value={sec.question_type}
                    onChange={(e) =>
                      updateSection(sec.local_id, {
                        question_type: e.target.value,
                      })
                    }
                  >
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="qb-field qb-field--marks">
                  <span>Count</span>
                  <input
                    type="number"
                    min={0}
                    value={numInputValue(sec.question_count)}
                    onChange={(e) =>
                      handleNumChange(e.target.value, (n) =>
                        updateSection(sec.local_id, { question_count: n })
                      )
                    }
                    onBlur={() =>
                      handleNumBlur(sec.question_count, (n) =>
                        updateSection(sec.local_id, { question_count: n })
                      )
                    }
                  />
                </label>
                <label className="qb-field qb-field--marks">
                  <span>Marks each</span>
                  <input
                    type="number"
                    min={0}
                    value={numInputValue(sec.marks_each)}
                    onChange={(e) =>
                      handleNumChange(e.target.value, (n) =>
                        updateSection(sec.local_id, { marks_each: n })
                      )
                    }
                    onBlur={() =>
                      handleNumBlur(sec.marks_each, (n) =>
                        updateSection(sec.local_id, { marks_each: n })
                      )
                    }
                  />
                </label>
                <label className="qb-field qb-field--chapter qb-field--instructions">
                  <span>Section instructions</span>
                  <input
                    value={sec.section_instructions}
                    onChange={(e) =>
                      updateSection(sec.local_id, {
                        section_instructions: e.target.value,
                      })
                    }
                    placeholder="Answer all. Each carries 1 mark."
                  />
                </label>
              </div>
            </div>
          </article>
        ))}
      </div>

      <Banner ok={marksMatch}>
        Grand total {grandTotal} vs Max marks {meta.max_marks || 0}
        {marksMatch
          ? " — totals match."
          : " — soft warning only (choice papers may over-provision)."}
      </Banner>

      <div className="pb-actions">
        <button
          type="button"
          className="qb-btn qb-btn--primary"
          disabled={!canContinue}
          onClick={onContinue}
        >
          {saving ? "Saving…" : "Save structure & continue"}
        </button>
      </div>
    </div>
  );
}

//Created By Cursor
function AssemblyStage({
  paper,
  setPaper,
  className,
  subjectName,
  onBack,
}) {
  const history = useHistory();
  const [pickerSection, setPickerSection] = useState(null);
  const [busy, setBusy] = useState(false);
  const schoolCode = SessionManager.getSchoolCode();
  const staff = SessionManager.getStaffDetails() || {};
  const statusKey = String(paper.status || "draft").toLowerCase();
  const statusStyle = STATUS_STYLES[statusKey] || STATUS_STYLES.draft;
  const locked = statusKey === "locked" || statusKey === "approved";

  const allComplete = useMemo(
    () =>
      (paper.sections || []).every(
        (s) => (s.items || []).length >= Number(s.question_count || 0)
      ),
    [paper]
  );

  const fillSummary = useMemo(() => {
    const sections = paper.sections || [];
    let filled = 0;
    let need = 0;
    sections.forEach((s) => {
      filled += (s.items || []).length;
      need += Number(s.question_count || 0);
    });
    return { filled, need };
  }, [paper]);

  //Created By Cursor
  const refreshPaper = useCallback(async () => {
    if (!paper?.id || !schoolCode) return null;
    const res = await axios.get(`${API_BASE()}getQuestionPaper.php`, {
      params: { school_code: schoolCode, id: paper.id },
    });
    if (res.data?.api_status === "OK" && res.data.data) {
      setPaper(res.data.data);
      return res.data.data;
    }
    return null;
  }, [paper?.id, schoolCode, setPaper]);

  //Created By Cursor — normalize API paper payload (handles nested {paper} shapes)
  const unwrapPaperPayload = (data) => {
    if (!data || typeof data !== "object") return null;
    if (Array.isArray(data.sections) && data.id != null) return data;
    if (data.paper && Array.isArray(data.paper.sections)) return data.paper;
    return null;
  };

  //Created By Cursor
  const addManual = useCallback(
    async (question) => {
      const sectionId = pickerSection?.id;
      const paperId = paper?.id;
      const questionId = question?.id;
      if (!sectionId || !paperId || !questionId) {
        myToast.notify_warning("Missing paper or question details.");
        return;
      }
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        return;
      }
      setBusy(true);
      try {
        // Form body — reliable for PHP $_POST on shared hosts
        const body = new URLSearchParams();
        body.set("school_code", String(schoolCode));
        body.set("paper_id", String(paperId));
        body.set("section_id", String(sectionId));
        body.set("question_bank_id", String(questionId));

        const res = await axios.post(
          `${API_BASE()}fillQuestionPaperManual.php`,
          body,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          }
        );

        let nextPaper = unwrapPaperPayload(res.data?.data);
        if (res.data?.api_status === "OK" && !nextPaper) {
          // Insert may have worked — reload from server
          nextPaper = await refreshPaper();
        }

        if (res.data?.api_status === "OK" && nextPaper) {
          setPaper(nextPaper);
          const sec = (nextPaper.sections || []).find(
            (s) => String(s.id) === String(sectionId)
          );
          if (sec && (sec.items || []).length >= Number(sec.question_count)) {
            setPickerSection(null);
          } else if (sec) {
            setPickerSection(sec);
          } else {
            setPickerSection((prev) => prev);
          }
          myToast.notify_success("Question added.");
        } else {
          myToast.notify_warning(
            res.data?.api_remark ||
              res.data?.message ||
              "Could not add question."
          );
        }
      } catch (err) {
        console.error("[PaperBuilder] addManual error:", err);
        myToast.notify_warning(
          err?.response?.data?.api_remark ||
            err?.response?.data?.message ||
            "Could not add question."
        );
      } finally {
        setBusy(false);
      }
    },
    [pickerSection, paper?.id, schoolCode, refreshPaper, setPaper]
  );

  //Created By Cursor
  const autoFill = async (section) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_BASE()}autoGeneratePaperSection.php`,
        {
          school_code: schoolCode,
          paper_id: paper.id,
          section_id: section.id,
        }
      );
      if (res.data?.api_status === "OK") {
        setPaper(res.data.data);
        const filled = res.data.data.sections.find(
          (s) => String(s.id) === String(section.id)
        );
        const count = (filled?.items || []).length;
        if (count < Number(section.question_count)) {
          myToast.notify_warning(
            `Only ${count} of ${section.question_count} found in bank.`
          );
        } else {
          myToast.notify_success("Section filled.");
        }
      } else {
        myToast.notify_warning(res.data?.api_remark || "Auto-fill failed.");
      }
    } catch (err) {
      console.error(err);
      myToast.notify_warning("Auto-fill failed.");
    } finally {
      setBusy(false);
    }
  };

  //Created By Cursor
  const removeItem = async (section, item) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API_BASE()}removeQuestionPaperItem.php`,
        {
          school_code: schoolCode,
          paper_id: paper.id,
          section_id: section.id,
          item_id: item.id,
        }
      );
      if (res.data?.api_status === "OK") {
        setPaper(res.data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
    }
  };

  //Created By Cursor
  const submit = async () => {
    if (!paper?.id) {
      myToast.notify_warning("Paper id is missing.");
      return;
    }
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    if (statusKey !== "draft") {
      myToast.notify_warning("Only draft papers can be submitted.");
      return;
    }
    if (!allComplete) {
      myToast.notify_warning(
        `Fill all sections first (${fillSummary.filled}/${fillSummary.need} questions).`
      );
      return;
    }
    setBusy(true);
    try {
      const body = new URLSearchParams();
      body.set("school_code", String(schoolCode));
      body.set("paper_id", String(paper.id));
      body.set("actioned_by", String(staff.staff_id || ""));
      const res = await axios.post(`${API_BASE()}submitQuestionPaper.php`, body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data?.api_status === "OK") {
        const next =
          unwrapPaperPayload(res.data.data) ||
          (await refreshPaper()) ||
          res.data.data;
        if (next) setPaper(next);
        myToast.notify_success("Submitted for approval.");
      } else {
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Submit failed."
        );
      }
    } catch (err) {
      console.error("[PaperBuilder] submit error:", err);
      myToast.notify_warning(
        err?.response?.data?.api_remark ||
          err?.response?.data?.message ||
          "Submit failed."
      );
    } finally {
      setBusy(false);
    }
  };

  //Created By Cursor
  const approve = async () => {
    if (!paper?.id) {
      myToast.notify_warning("Paper id is missing.");
      return;
    }
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    if (statusKey !== "submitted") {
      myToast.notify_warning("Only submitted papers can be approved.");
      return;
    }
    setBusy(true);
    try {
      const body = new URLSearchParams();
      body.set("school_code", String(schoolCode));
      body.set("paper_id", String(paper.id));
      body.set("actioned_by", String(staff.staff_id || ""));
      const res = await axios.post(`${API_BASE()}approveQuestionPaper.php`, body, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (res.data?.api_status === "OK") {
        const next =
          unwrapPaperPayload(res.data.data) ||
          (await refreshPaper()) ||
          res.data.data;
        if (next) setPaper(next);
        myToast.notify_success("Paper approved and locked.");
      } else {
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Approve failed."
        );
      }
    } catch (err) {
      console.error("[PaperBuilder] approve error:", err);
      myToast.notify_warning(
        err?.response?.data?.api_remark ||
          err?.response?.data?.message ||
          "Approve failed."
      );
    } finally {
      setBusy(false);
    }
  };

  //Created By Cursor
  const openPrint = () => {
    if (!paper?.id) {
      myToast.notify_warning("Save the paper before printing.");
      return;
    }
    history.push(`/AdminMain/PrintQuestionPaper/${encodeURIComponent(paper.id)}`);
  };

  return (
    <div className="pb-stage">
      <div className="pb-stage__head">
        <div>
          <h2 className="pb-stage__title">Assemble questions</h2>
          <p className="pb-meta-line">
            {paper.exam_name} · {className} · {subjectName}
          </p>
          <div className="pb-meta-pills">
            <Pill bg={statusStyle.bg} fg={statusStyle.fg}>
              {statusStyle.label}
            </Pill>
            <Pill bg="#EEF1F8" fg={BRAND.navy}>
              {fillSummary.filled}/{fillSummary.need} filled
            </Pill>
            <Pill bg="#EAF9F7" fg="#0F8A7E">
              {paper.max_marks} marks
            </Pill>
          </div>
        </div>
        <button type="button" className="qb-btn qb-btn--ghost" onClick={onBack}>
          Edit structure
        </button>
      </div>

      {(paper.sections || []).map((sec, secIdx) => {
        const filled = (sec.items || []).length;
        const need = Number(sec.question_count || 0);
        const complete = filled >= need;
        const pct = need > 0 ? Math.min(100, Math.round((filled / need) * 100)) : 0;
        return (
          <article key={sec.id} className="pb-section-card pb-section-card--assemble">
            <div className="pb-section-card__top">
              <span className="pb-section-card__badge" aria-hidden>
                {String.fromCharCode(65 + secIdx)}
              </span>
              <div className="pb-section-card__title">
                <strong>
                  {sec.section_label}{" "}
                  {complete ? (
                    <span className="pb-complete">Complete</span>
                  ) : null}
                </strong>
                <span>
                  {TYPE_LABELS[sec.question_type] || sec.question_type}: {filled}{" "}
                  of {need} · {sec.marks_each} mark(s) each
                </span>
              </div>
            </div>
            <div className="pb-progress" aria-hidden>
              <div className="pb-progress__bar" style={{ width: `${pct}%` }} />
            </div>
            <div className="pb-section-actions">
              <button
                type="button"
                className="qb-btn qb-btn--primary"
                disabled={busy || complete || locked}
                onClick={() => setPickerSection(sec)}
              >
                <FaPlus /> Add manually
              </button>
              <button
                type="button"
                className="qb-btn qb-btn--teal"
                disabled={busy || complete || locked}
                onClick={() => autoFill(sec)}
              >
                <FaMagic /> Auto-generate
              </button>
            </div>
            <div className="qb-list">
              {(sec.items || []).map((item, idx) => (
                <article key={item.id} className="qb-card">
                  <div className="qb-card__body">
                    <p className="qb-card__text">
                      {idx + 1}. {item.question_text}
                    </p>
                    <div className="qb-card__meta">
                      {item.difficulty ? (
                        <Pill bg="#EEF1F8" fg={BRAND.navy}>
                          {item.difficulty}
                        </Pill>
                      ) : null}
                      {item.chapter ? (
                        <Pill bg="#FFF6E9" fg="#B5750B">
                          {item.chapter}
                        </Pill>
                      ) : null}
                    </div>
                  </div>
                  {statusKey === "draft" ? (
                    <button
                      type="button"
                      className="qb-icon-btn qb-icon-btn--danger"
                      disabled={busy}
                      title="Remove"
                      onClick={() => removeItem(sec, item)}
                    >
                      <FaTrash />
                    </button>
                  ) : null}
                </article>
              ))}
              {filled < need
                ? Array.from({ length: need - filled }).map((_, i) => (
                    <div key={`empty-${i}`} className="pb-empty-slot">
                      Empty slot {filled + i + 1}
                    </div>
                  ))
                : null}
            </div>
          </article>
        );
      })}

      <div className="pb-actions pb-actions--row">
        <button
          type="button"
          className="qb-btn qb-btn--ghost"
          onClick={refreshPaper}
          disabled={busy}
        >
          <FaSyncAlt /> Refresh
        </button>
        <button
          type="button"
          className="qb-btn qb-btn--primary"
          disabled={!allComplete || busy || statusKey !== "draft"}
          title={
            statusKey !== "draft"
              ? "Only draft papers can be submitted"
              : !allComplete
                ? `Fill all sections (${fillSummary.filled}/${fillSummary.need})`
                : "Submit for approval"
          }
          onClick={submit}
        >
          Submit for approval
        </button>
        {!allComplete && statusKey === "draft" ? (
          <span className="pb-actions__hint">
            Fill {fillSummary.need - fillSummary.filled} more question
            {fillSummary.need - fillSummary.filled === 1 ? "" : "s"} to submit
          </span>
        ) : null}
        <button
          type="button"
          className="qb-btn qb-btn--teal"
          disabled={busy || statusKey !== "submitted"}
          onClick={approve}
        >
          Approve & lock
        </button>
        <button
          type="button"
          className="qb-btn qb-btn--ghost"
          onClick={openPrint}
          disabled={!paper?.id || busy}
        >
          <FaPrint /> Print Question Paper
        </button>
      </div>

      {pickerSection ? (
        <QuestionPickerModal
          paper={paper}
          section={pickerSection}
          busy={busy}
          onClose={() => setPickerSection(null)}
          onPick={addManual}
        />
      ) : null}
    </div>
  );
}

//Created By Cursor
export default function PaperBuilder() {
  const history = useHistory();
  const { id: routePaperId } = useParams();
  const loggedIn = localStorage.getItem("token") != null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stage, setStage] = useState(1);
  const [classes, setClasses] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [paper, setPaper] = useState(null);
  const [meta, setMeta] = useState({
    exam_name: "",
    class_id: "",
    subject_id: "",
    max_marks: 50,
    duration_minutes: 0,
    exam_date: "",
    general_instructions:
      "All questions are compulsory.\nFigures to the right indicate marks.",
    blueprint_name: "",
    save_blueprint: true,
  });
  const [sections, setSections] = useState([
    {
      ...emptySection(0),
      question_type: "mcq",
      question_count: 10,
      marks_each: 1,
    },
    {
      ...emptySection(1),
      question_type: "short",
      question_count: 10,
      marks_each: 2,
    },
    {
      ...emptySection(2),
      question_type: "long",
      question_count: 4,
      marks_each: 5,
    },
  ]);

  //Created By Cursor
  useEffect(() => {
    window.scrollTo(0, 0);
    const current = SessionManager.getCurrentSession()?.[0];
    setSessionId(current?.session_id || "");
  }, []);

  //Created By Cursor
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        setLoading(false);
        return;
      }
      try {
        const [classRes, subjectRes] = await Promise.all([
          axios.get(`${API_BASE()}getQuestionBankClasses.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId || undefined,
            },
          }),
          axios.get(`${API_BASE()}getQuestionBankClassSubjects.php`, {
            params: {
              school_code: schoolCode,
              session_id: sessionId || undefined,
            },
          }),
        ]);
        if (cancelled) return;
        const cls =
          classRes.data?.api_status === "OK" && Array.isArray(classRes.data.data)
            ? classRes.data.data
            : [];
        const subs =
          subjectRes.data?.api_status === "OK" &&
          Array.isArray(subjectRes.data.data)
            ? subjectRes.data.data
            : [];
        setClasses(cls);
        setClassSubjects(subs);

        if (routePaperId) {
          const paperRes = await axios.get(`${API_BASE()}getQuestionPaper.php`, {
            params: { school_code: schoolCode, id: routePaperId },
          });
          if (cancelled) return;
          if (paperRes.data?.api_status === "OK" && paperRes.data.data) {
            const p = paperRes.data.data;
            setPaper(p);
            setMeta((m) => ({
              ...m,
              exam_name: p.exam_name || "",
              class_id: p.class_id || "",
              subject_id: p.subject_id || "",
              max_marks: Number(p.max_marks || m.max_marks),
              duration_minutes: Number(p.duration_minutes || m.duration_minutes),
              exam_date: p.exam_date || "",
              general_instructions:
                p.general_instructions || m.general_instructions,
            }));
            setStage(2);
          } else {
            myToast.notify_warning(
              paperRes.data?.api_remark || "Paper not found."
            );
            history.replace("/AdminMain/QuestionPapers");
          }
        } else if (!meta.class_id && cls[0]) {
          const nextSubs = subjectsForClass(cls[0].class_id, subs);
          setMeta((m) => ({
            ...m,
            class_id: cls[0].class_id,
            subject_id: nextSubs[0]?.subject_id || "",
          }));
        }
      } catch (err) {
        console.error("[PaperBuilder] lookup error:", err);
        myToast.notify_warning("Could not load classes/subjects.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, routePaperId]);

  const className = useMemo(() => {
    const cid = paper?.class_id || meta.class_id;
    const c = classes.find((x) => String(x.class_id) === String(cid));
    return c?.class_name || cid;
  }, [classes, meta.class_id, paper]);

  const subjectName = useMemo(() => {
    const cid = paper?.class_id || meta.class_id;
    const sid = paper?.subject_id || meta.subject_id;
    const rows = subjectsForClass(cid, classSubjects);
    const s = rows.find((x) => String(x.subject_id) === String(sid));
    return subjectLabel(s) || sid;
  }, [classSubjects, meta.class_id, meta.subject_id, paper]);

  const activeStage = stage === 1 || !paper ? 1 : 2;

  //Created By Cursor
  const createPaper = async () => {
    const schoolCode = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails() || {};
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      return;
    }
    setSaving(true);
    try {
      let blueprintId = null;
      if (meta.blueprint_name && String(meta.blueprint_name).trim()) {
        const bpRes = await axios.post(`${API_BASE()}addPaperBlueprint.php`, {
          school_code: schoolCode,
          name: meta.blueprint_name.trim(),
          class_id: meta.class_id,
          subject_id: meta.subject_id,
          max_marks: toNum(meta.max_marks),
          duration_minutes: toNum(meta.duration_minutes),
          general_instructions: meta.general_instructions,
          created_by: staff.staff_id || "",
          sections: sections.map((s) => ({
            section_label: s.section_label,
            question_type: s.question_type,
            question_count: toNum(s.question_count),
            marks_each: toNum(s.marks_each),
            section_instructions: s.section_instructions,
          })),
        });
        if (bpRes.data?.api_status === "OK") {
          blueprintId = bpRes.data.data?.id || null;
        }
      }

      const res = await axios.post(`${API_BASE()}addQuestionPaper.php`, {
        school_code: schoolCode,
        blueprint_id: blueprintId || "",
        exam_name: meta.exam_name,
        class_id: meta.class_id,
        subject_id: meta.subject_id,
        max_marks: toNum(meta.max_marks),
        duration_minutes: toNum(meta.duration_minutes),
        exam_date: meta.exam_date || "",
        general_instructions: meta.general_instructions,
        created_by: staff.staff_id || "",
        sections: sections.map((s) => ({
          section_label: s.section_label,
          question_type: s.question_type,
          question_count: toNum(s.question_count),
          marks_each: toNum(s.marks_each),
          section_instructions: s.section_instructions,
        })),
      });

      if (res.data?.api_status === "OK" && res.data.data) {
        setPaper(res.data.data);
        setStage(2);
        myToast.notify_success("Paper structure saved.");
        if (res.data.data.id) {
          history.replace(`/AdminMain/PaperBuilder/${res.data.data.id}`);
        }
      } else {
        myToast.notify_warning(
          res.data?.api_remark || res.data?.message || "Failed to create paper."
        );
      }
    } catch (err) {
      console.error("[PaperBuilder] create error:", err);
      myToast.notify_warning("Failed to create paper.");
    } finally {
      setSaving(false);
    }
  };

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page qb-page pb-page"
      style={{ background: BRAND.bg }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="qb-crumb-link"
                onClick={() => history.push("/AdminMain/QuestionPapers")}
              >
                Question Paper
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>
                {routePaperId ? "Paper details" : "Paper Builder"}
              </span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaLayerGroup />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">
                {routePaperId ? "Paper details" : "Paper Builder"}
              </h1>
              <p className="sm-hero__sub">
                Structure the paper, fill from the bank, then approve & export.
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <StageStepper stage={activeStage} />
            <button
              type="button"
              className="qb-hero-btn qb-hero-btn--ghost"
              onClick={() => history.push("/AdminMain/QuestionPapers")}
            >
              All papers
            </button>
          </div>
        </header>

        <div className="qb-content qb-add-content">
          {loading ? (
            <MyLoader />
          ) : activeStage === 1 ? (
            <StructureStage
              classes={classes}
              classSubjects={classSubjects}
              meta={meta}
              setMeta={setMeta}
              sections={sections}
              setSections={setSections}
              onContinue={createPaper}
              saving={saving}
            />
          ) : (
            <AssemblyStage
              paper={paper}
              setPaper={setPaper}
              className={className}
              subjectName={subjectName}
              onBack={() => setStage(1)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
